import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaHeadset, FaTools, FaBullhorn, FaUserCheck, FaCalendarAlt, FaUsers, FaChartLine } from 'react-icons/fa';
import logo from '../../Images/logo.png';
import styles from './Navbar.module.css';

const ProductDropdown = ({ isOpen, toggleDropdown, handleMouseEnter, handleMouseLeave, handleKeyDown }) => {
  const products = [
    {
      label: 'Customer Support',
      icon: <FaHeadset />,
      subItems: [
        { label: 'Live Chat Support', href: '/ai-support/live-chat' },
        { label: 'Ticket Resolution', href: '/ai-support/ticket-resolution' },
        { label: 'Customer Feedback', href: '/ai-support/feedback' },
      ],
    },
    {
      label: 'AI Tech Support',
      icon: <FaTools />,
      subItems: [
        { label: 'Technical Troubleshooting', href: '/ai-tech/troubleshooting' },
        { label: 'Software Assistance', href: '/ai-tech/software' },
        { label: 'Hardware Support', href: '/ai-tech/hardware' },
      ],
    },
    {
      label: 'AI Sales Agent',
      icon: <FaBullhorn />,
      subItems: [
        { label: 'Lead Qualification', href: '/ai-sales/lead-qualification' },
        { label: 'Sales Pitch Automation', href: '/ai-sales/pitch' },
        { label: 'Follow-Up Management', href: '/ai-sales/follow-up' },
      ],
    },
    {
      label: 'AI Interviewer',
      icon: <FaUserCheck />,
      subItems: [
        { label: 'Candidate Screening', href: '/ai-interviewer/screening' },
        { label: 'Interview Scheduling', href: '/ai-interviewer/scheduling' },
        { label: 'Assessment Analysis', href: '/ai-interviewer/assessment' },
      ],
    },
    {
      label: 'AI Booking Agent',
      icon: <FaCalendarAlt />,
      subItems: [
        { label: 'Appointment Booking', href: '/ai-booking/appointment' },
        { label: 'Reservation Management', href: '/ai-booking/reservation' },
        { label: 'Calendar Integration', href: '/ai-booking/calendar' },
      ],
    },
    {
      label: 'AI Recruiter',
      icon: <FaUsers />,
      subItems: [
        { label: 'Talent Sourcing', href: '/ai-recruiter/sourcing' },
        { label: 'Resume Screening', href: '/ai-recruiter/resume' },
        { label: 'Candidate Outreach', href: '/ai-recruiter/outreach' },
      ],
    },
    {
      label: 'AI Lead Agent',
      icon: <FaChartLine />,
      subItems: [
        { label: 'Lead Generation', href: '/ai-lead/generation' },
        { label: 'Lead Nurturing', href: '/ai-lead/nurturing' },
        { label: 'CRM Integration', href: '/ai-lead/crm' },
      ],
    },
  ];

  return (
    <div
      className={styles.navItem}
      onMouseEnter={() => handleMouseEnter('products')}
      onMouseLeave={() => handleMouseLeave('products')}
    >
      <button
        className={`${styles.navLink} ${styles.dropdownToggle}`}
        onClick={() => toggleDropdown('products')}
        onKeyDown={(e) => handleKeyDown(e, 'products')}
        aria-expanded={isOpen}
        aria-controls="dropdown-products"
      >
        Products
        <FaChevronDown className={styles.chevronIcon} />
      </button>
      <div
        id="dropdown-products"
        className={`${styles.dropdown} ${isOpen ? styles.show : ''} ${styles.productsDropdown}`}
      >
        {products.map((dropdownItem) => (
          <div key={dropdownItem.label} className={styles.dropdownGroup}>
            <div className={styles.dropdownHeader}>
              {dropdownItem.icon}
              <span className={styles.dropdownHeaderText}>{dropdownItem.label}</span>
            </div>
            {dropdownItem.subItems.map((subItem) => (
              <NavLink
                key={subItem.label}
                to={subItem.href}
                target="_blank"
                className={({ isActive }) =>
                  `${styles.dropdownItem} ${styles.subItem} ${isActive ? styles.active : ''}`
                }
                onClick={() => toggleDropdown('products')}
              >
                {subItem.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const AboutUsDropdown = ({ isOpen, toggleDropdown, handleMouseEnter, handleMouseLeave, handleKeyDown }) => {
  const aboutUsItems = [
    { label: 'Why Us', href: '/why-us' },
    // { label: 'News & Media', href: '/news' },
    { label: 'Careers', href: '/careers' },
    { label: 'Contact Us', href: '/contact' },
  ];

  return (
    <div
      className={styles.navItem}
      onMouseEnter={() => handleMouseEnter('aboutus')}
      onMouseLeave={() => handleMouseLeave('aboutus')}
    >
      <button
        className={`${styles.navLink} ${styles.dropdownToggle}`}
        onClick={() => toggleDropdown('aboutus')}
        onKeyDown={(e) => handleKeyDown(e, 'aboutus')}
        aria-expanded={isOpen}
        aria-controls="dropdown-aboutus"
      >
        About Us
        <FaChevronDown className={styles.chevronIcon} />
      </button>
      <div
        id="dropdown-aboutus"
        className={`${styles.dropdown} ${isOpen ? styles.show : ''} ${styles.aboutusDropdown}`}
      >
        {aboutUsItems.map((dropdownItem) => (
          <NavLink
            key={dropdownItem.label}
            to={dropdownItem.href}
            className={({ isActive }) =>
              `${styles.dropdownItem} ${isActive ? styles.active : ''}`
            }
            onClick={() => toggleDropdown('aboutus')}
          >
            {dropdownItem.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({
    products: false,
    aboutus: false,
  });
  const dropdownRefs = useRef({});
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    localStorage.clear();
  };

  const toggleDropdown = (menu) => {
    setDropdownOpen((prev) => {
      const newState = {
        products: false,
        aboutus: false,
      };
      newState[menu] = !prev[menu];
      return newState;
    });
  };

  const handleMouseEnter = (menu) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [menu]: true,
    }));
  };

  const handleMouseLeave = (menu) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [menu]: false,
    }));
  };

  const handleKeyDown = (e, menu) => {
    if (e.key === 'Enter' || e.key === ' ') {
      toggleDropdown(menu);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setDropdownOpen((prev) => ({ ...prev, [menu]: false }));
    }
  };

  const handleOutsideClick = (e) => {
    let isOutsideClick = true;

    Object.values(dropdownRefs.current).forEach((dropdownRef) => {
      if (dropdownRef && dropdownRef.contains(e.target)) {
        isOutsideClick = false;
      }
    });

    if (isOutsideClick) {
      setDropdownOpen({
        products: false,
        aboutus: false,
      });
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <nav className={styles.navbar}>
      <div className={styles.navbarContainer}>
        <div className={styles.logoContainer} onClick={() => navigate("/")}>
          <img
            className={styles.logoimg}
            src={logo}
            alt="Logo"
            loading="lazy"
          />
        </div>
        <div className={`${styles.navLinks} ${isOpen ? styles.showMenu : ""}`}>
          <div ref={(el) => (dropdownRefs.current["products"] = el)}>
            <ProductDropdown
              isOpen={dropdownOpen.products}
              toggleDropdown={toggleDropdown}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
              handleKeyDown={handleKeyDown}
            />
          </div>
          <div ref={(el) => (dropdownRefs.current["aboutus"] = el)}>
            <AboutUsDropdown
              isOpen={dropdownOpen.aboutus}
              toggleDropdown={toggleDropdown}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
              handleKeyDown={handleKeyDown}
            />
          </div>
          <Link to="/demo-form" className={styles.signin} onClick={toggleMenu}>
            Book Demo
          </Link>
          <Link to="/login" className={styles.signin} onClick={toggleMenu}>
            Sign in
          </Link>
        </div>
        <div className={styles.hamburgerContainer}>
          <button onClick={toggleMenu} className={styles.hamburger}>
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;